import React, {useContext, useState} from 'react'
import {ParsedQuery} from 'query-string'
import {FormattedMessage, useIntl} from 'react-intl'
import {booleanFromQuery, booleanToQuery} from '../../../helpers/coreUtil'
import {IValuesToQuery, useQuery, useQueryObj} from '../../hocs/hooks'
import {Form} from 'react-final-form'
import AngleRight from '../../../assets/icons/AngleRight.svg'
import Times from '../../../assets/icons/Times.svg'
import {carFreeFacilityList, carNonFreeFacilityList, carTypes} from '../../../config/constants'
import {carFreeFacilityMessages, carNonFreeFacilityMessages} from '../../../config/commonMessages'
import {RouterContext} from '../../containers/Context'
import BackDrop from '../../containers/BackDrop'
import searchStyles from './CoverPC.scss'
import styles from './CarsSearch.scss'
import CheckField from '../../fields/custom/CheckField'
import RadioField from '../../fields/custom/RadioField'

const queryToValues = (
  {
    keyword = ' ',
    ...query
  }: ParsedQuery<string>) => ({
  keyword,
  ...query,
  ...Object.fromEntries([
    ...carFreeFacilityList,
    ...carNonFreeFacilityList,
  ].map(fac => [fac, booleanFromQuery(query[fac])])),
})

const valuesToQuery: IValuesToQuery<typeof queryToValues> = (
  {
    keyword,
    ...query
  }) => ({
  keyword: keyword === ' ' ? undefined : keyword,
  ...query,
  ...Object.fromEntries([
    ...carFreeFacilityList,
    ...carNonFreeFacilityList,
  ].map(fac => [fac, booleanToQuery(query[fac])]))
})

const CarsSearch = ({onClose}: {
  onClose: () => any
}) => {
  const {history} = useContext(RouterContext)!
  const [isOpenFree, setIsOpenFree] = useState(false)
  const [isOpenNonFree, setIsOpenNonFree] = useState(false)
  const [isOpenCarType, setIsOpenCarType] = useState(false)
  const initialValues = useQuery(queryToValues)
  const [values, setValues] = useState(initialValues)
  const queryObj = useQueryObj(queryToValues, valuesToQuery)
  const intl = useIntl()
  const carTypeList = [
    {title: intl.formatMessage({ id: 'carTypes.all' }), name: ' '},
    ...carTypes.map(carType => {
      const title = intl.formatMessage({ id: carType.category })
      const name = intl.formatMessage({ id: carType.nameCategory })
      return {
        title,
        name,
      }
    })
  ]
  const onCarSubmit = (newValues: any) => {
    if (newValues.page) newValues.page = undefined
    history.push(queryObj({
      ...newValues,
      ...Object.fromEntries([
        ...carFreeFacilityList.map(key => [key, newValues[key]]),
        ...carNonFreeFacilityList.map(key => [key, newValues[key]]),
      ])
    }))
  }
  return <Form onSubmit={onCarSubmit} initialValues={values}>
    {({handleSubmit, form}) => {
      const resetSearch = () => {
        setValues({
          keyword: ' ',
        })
        form.reset()
      }
      return <>
      <form onSubmit={handleSubmit}
            className={searchStyles.searchBox}
            id="refineSearch">
        <BackDrop onBackgroundClicked={onClose}>
          <div tabIndex={-1} role="dialog" className={['m-2 modal-dialog-scrollable', styles.modalPanel].join(' ')}>
            <div className="modal-content w-100">
              <div className="modal-header py-4">
                <div className="w-100 text-center">
                  <h3 className={['m-0 font-weight-medium'].join(' ')}>
                    <FormattedMessage id="carsSearch.title" />
                  </h3>
                </div>
                <Times className={styles.cross} onClick={onClose}/>
              </div>

              <div className="modal-body m-3">
                <div className="form-group pb-md-3">
                  <div className="font-weight-bold mb-2">
                    <span
                      className="c-pointer"
                      onClick={() => setIsOpenCarType(!isOpenCarType)}
                    >
                      <AngleRight
                        className={['mb-1 mr-2 fa font-size-75', isOpenCarType && styles.rotate]
                          .filter(Boolean).join(' ')}
                      />
                      <FormattedMessage id="carTypes.carType"/>
                    </span>
                  </div>
                  {isOpenCarType
                    && <>
                    {[...Array(Math.ceil(carTypeList.length / 3)).keys()]
                      .map(index => (<div className="row mb-md-1" key={index}>
                        {carTypeList.slice(index * 3, index * 3 + 3)
                          .map(carType => <div className="col-12 mb-2" key={carType.name}>
                              <RadioField name="keyword" value={carType.name} isVan>
                                { carType.title }
                              </RadioField>
                          </div>
                          )}
                    </div>))}
                  </>}
                </div>
                <div className="form-group pb-md-3">
                  <div className="font-weight-bold mb-2">
                    <span
                      className="c-pointer"
                      onClick={() => setIsOpenFree(!isOpenFree)}>
                      <AngleRight
                        className={['mb-1 mr-2 fa font-size-75', isOpenFree && styles.rotate].filter(Boolean).join(' ')}
                      />
                      <FormattedMessage id="station.freeFacility"/>
                    </span>
                  </div>
                  {isOpenFree
                    && <>
                    {[...Array(Math.ceil(carFreeFacilityList.length / 3)).keys()]
                      .map(index => (<div className="row mb-md-1" key={index}>
                        {carFreeFacilityList.slice(index * 3, index * 3 + 3)
                          .map(facility => <div className="col-12 mb-2" key={facility}>
                              <CheckField name={facility} isVan>
                                <FormattedMessage {...carFreeFacilityMessages[facility]}/>
                              </CheckField>
                            </div>
                          )}
                      </div>))}
                  </>}
                </div>

                <div className="form-group">
                  <div className="font-weight-bold mb-2">
                    <span
                      className="c-pointer"
                      onClick={() => setIsOpenNonFree(!isOpenNonFree)}
                    >
                      <AngleRight
                        className={['mb-1 mr-2 fa font-size-75', isOpenNonFree && styles.rotate]
                          .filter(Boolean).join(' ')}
                      />
                      <FormattedMessage id="station.nonFreeFacility"/>
                    </span>
                  </div>
                  {isOpenNonFree
                    && <>
                    {[...Array(Math.ceil(carNonFreeFacilityList.length / 3)).keys()]
                      .map(index => (<div className="row mb-md-1" key={index}>
                        {carNonFreeFacilityList.slice(index * 3, index * 3 + 3)
                          .map(facility => <div className="col-12 mb-2" key={facility}>
                              <CheckField name={facility} isVan>
                                <FormattedMessage {...carNonFreeFacilityMessages[facility]}/>
                              </CheckField>
                          </div>
                          )}
                    </div>))}
                  </>}
                </div>
              </div>

              <div className="modal-footer py-4">
                <div className="w-100 text-center">
                  <button
                    onClick={onClose}
                    className={['btn px-4 py-0 font-weight-bold btn-van mb-2', styles.button].join(' ')}
                    type="submit"
                  >
                    <FormattedMessage id="carsSearch.close" defaultMessage="Refine search"/>
                  </button>
                  <div
                    onClick={resetSearch}
                    className={
                      [
                        'c-pointer d-flex btn align-items-center px-4 py-0 font-weight-bold btn-outline-van',
                        styles.button
                      ].join(' ')}
                  >
                    <div className="text-center w-100">
                      <FormattedMessage id="carsSearch.clear" defaultMessage="Clear search condition"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BackDrop>
      </form>
    </>
    }}
  </Form>
}
CarsSearch.displayName = 'CarsSearch'
export default CarsSearch
